<template>
  <div
      v-loading="loading"
      class="order-return-review-customer-details flex flex-auto flex-col">
    <div class="title">
      Review customer details and address
    </div>
    <div class="description">
      Review the customer details before proceeding.
    </div>
    <el-alert
        v-if="!allCustomerDataFilled && !loading"
        :closable="false"
        :title="alertComponent.title"
        type="warning"
        :description="alertComponent.description"
        show-icon />
    <el-alert
        v-if="!isCustomerAuthorized"
        :closable="false"
        :title="findAlertByKey('customerAuthorizationRequired').title"
        type="warning"
        :description="findAlertByKey('customerAuthorizationRequired').description"
        show-icon />
    <div style="margin-top: 24px">
      <div class="section-header">
        <div class="flex-1">
          Customer Details
        </div>
        <a
            v-if="isCustomerAuthorized"
            class="edit-details-link"
            @click="$emit('update:editDetails', true)">
          Edit Customer Details
        </a>
      </div>
    </div>
    <div class="return-customer-details">
      <el-card shadow="never">
        <div class="flex flex-col">
          <div class="flex flex-row">
            <div class="flex-1">
              Name
            </div>
            <div>{{ customer.first_name }} {{ customer.last_name }}</div>
          </div>
          <div class="flex flex-row">
            <div class="flex-1">
              Phone Number
            </div>
            <div>{{ customer.phone_number }}</div>
          </div>
          <div class="flex flex-row">
            <div class="flex-1">
              Email
            </div>
            <div>{{ customer.email }}</div>
          </div>
          <div
              class="flex flex-row"
              style="margin-top: 20px">
            <div class="flex-1">
              Street
            </div>
            <div>{{ address.address1 }} {{ address.address2 }}</div>
          </div>
          <div
              class="flex flex-row">
            <div class="flex-1">
              City
            </div>
            <div>{{ address.city }}</div>
          </div>
          <div
              class="flex flex-row">
            <div class="flex-1">
              State/province/area
            </div>
            <div>{{ address.state }}</div>
          </div>
          <div
              class="flex flex-row">
            <div class="flex-1">
              Zip code
            </div>
            <div>{{ address.zip_code }}</div>
          </div>
          <div
              class="flex flex-row">
            <div class="flex-1">
              Country
            </div>
            <div>{{ address.country }}</div>
          </div>
        </div>
      </el-card>
    </div>
  </div>
</template>
<script>
import { isObfuscatedData } from "@/utils/utils";

export default {
  name: "ReviewCustomerDetailsAddress",
  props: {
    loading: {
      type: Boolean,
      required: true,
      default() {
        return false;
      },
    },
    customer: {
      type: Object,
      required: true,
      default() {
        return {
          first_name: "",
          last_name: "",
          phone_number: "",
          email: "",
        };
      },
    },
    address: {
      type: Object,
      required: true,
    },
    alertComponent: {
      type: Object,
      required: false,
      default() {
        return {
          title: "",
          description: "",
        };
      },
    },
  },
  data() {
    return {
      messages: [
        {
          key: "customerAuthorizationRequired",
          title: "Customer Authorization Required",
          description:
            "This account is not authorized to show PII information. Please authorize it on customer's page, you will not be able to continue with this request until then.",
        },
        {
          key: "customerDataMissing",
          title: "Customer Information Required",
          description:
            "Please edit customer information and enter the missing values",
        },
        {
          key: "addressElementMissing",
          title: "Address Information Required",
          description:
            "Please edit customer information to enter a shipping address",
        },
        {
          key: "customerPhoneNumberMissing",
          title: "Phone Number Required",
          description:
            "Please edit customer information to enter a valid phone number",
        },
      ],
    };
  },
  computed: {
    allCustomerDataFilled() {
      return !this.addressElementMissing && !this.customerPhoneNumberMissing;
    },
    addressElementMissing() {
      const { address1, city, country, state, zip_code } = this.address;
      return !address1 || !city || !country || !state || !zip_code;
    },
    customerPhoneNumberMissing() {
      return !this.customer.phone_number;
    },
    alertMessage() {
      const customerDataMissing =
        this.addressElementMissing && this.customerPhoneNumberMissing;

      if (customerDataMissing) {
        return this.findAlertByKey("customerDataMissing");
      } else if (this.addressElementMissing) {
        return this.findAlertByKey("addressElementMissing");
      } else if (this.customerPhoneNumberMissing) {
        return this.findAlertByKey("customerPhoneNumberMissing");
      }
      return null;
    },
  },
  watch: {
    allCustomerDataFilled: {
      immediate: true,
      handler(value) {
        this.$emit("update:filled", value);
      },
    },
    alertMessage: {
      immediate: true,
      handler(value) {
        if (value) {
          this.alertComponent.title = value.title;
          this.alertComponent.description = value.description;
        }
      },
    },
  },
  methods: {
    findAlertByKey(alertKey) {
      return this.messages.find((alert) => alert.key === alertKey);
    },
    isCustomerAuthorized() {
      return !this.isObfuscatedData(this.customer.email);
    },
    isObfuscatedData,
  },
};
</script>

<style>
.order-return-review-customer-details {
  gap: 8px;

  .title {
    height: 36px;
    font-style: normal;
    font-weight: 500;
    font-size: 24px;
    line-height: 36px;
    display: flex;
    align-items: flex-end;
    color: #424242;
    flex: none;
    align-self: stretch;
    flex-grow: 0;
  }

  .description {
    height: 24px;
    font-style: normal;
    font-weight: 300;
    font-size: 16px;
    line-height: 24px;
    display: flex;
    align-items: flex-end;
    color: #424242;
    flex: none;
    align-self: stretch;
    flex-grow: 0;
  }

  .section-header {
    height: 24px;
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    line-height: 24px;
    display: flex;
    align-items: flex-end;
    color: #6e6e6e;
    flex: none;
    flex-grow: 1;
  }

  .return-customer-details {
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 24px;
    color: #424242;
  }

  .edit-details-link {
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    line-height: 24px;
    text-align: right;
    text-decoration-line: underline;
    color: #1476ad;
  }
}
</style>

